import React, { useEffect, useRef, useState } from 'react'
import 'react-quill/dist/quill.snow.css';
import ReactQuill, { Quill } from 'react-quill';
import { useNavigate } from 'react-router-dom';
import ImgCrop from 'antd-img-crop';
import BlotFormatter from 'quill-blot-formatter';

import { ACTIVE, INACTIVE, PENDING } from '../../../../../Constants/activeStatusType';
import { DESTINATION_CONTAINER, handleDeleteBlob, handleImageUpload } from '../../../../../azure/blob';
import { editDestination, getDestinationById } from '../../../../../../redux/api/destinations.api';

import { Button, Col, Form, Image, Input, notification, Row, Select, Spin, Upload } from 'antd'
import { PlusOutlined } from '@ant-design/icons';

import { destinationTypes, months, weatherTypes } from '../../../../../Constants/itemTypes';

import './styles.scss'
import useDestinations from '../../../../../hooks/useDestinations';

const getOptionName = (value) => {
    return value
        .toLowerCase()
        .replace(/_./g, (match) => ' ' + match.charAt(1).toUpperCase())
        .replace(/^\w/, (c) => c.toUpperCase());
};

const destinationOptions = Object.keys(destinationTypes).map(key => {
    return {
        label: getOptionName(destinationTypes[key]),
        value: key
    };
});
const weatherOptions = Object.keys(weatherTypes).map(key => {
    return {
        label: getOptionName(weatherTypes[key]),
        value: key
    };
});
const monthOptions = Object.keys(months).map(key => {
    return {
        label: getOptionName(months[key]),
        value: key
    };
});

const quillModules = {
    toolbar: [
        [{ font: [] }],
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ align: [] }],
        ["bold", "italic", "underline", "strike"],
        [{ color: [] }, { background: [] }],
        [{ script: "sub" }, { script: "super" }],
        ["blockquote", "code-block"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link", "image", "video"],
        ["clean"],
        ["formula"],
        [{ size: ["small", false, "large", "huge"] }],
    ],
    clipboard: {
        matchVisual: false
    },
    imageResize: {
        parchment: Quill.import('parchment'),
        modules: ['Resize', 'DisplaySize']
    }
};


const getBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
    });


const EditDestination = () => {
    const [shortDescriptionCharCount, setShortDescriptionCharCount] = useState(0)
    const [longDescriptionCharCount, setLongDescriptionCharCount] = useState(0)
    const [fileList, setFileList] = useState([])
    const [videoList, setVideoList] = useState([])
    const [thumbnailFile, setThumbnailFile] = useState([]);
    const [previewImage, setPreviewImage] = useState('');
    const [previewOpen, setPreviewOpen] = useState(false);
    const [loading, setLoading] = useState(false)
    const [loadingText, setLoadingText] = useState('')
    const topRef = useRef(null);
    const [api, contextHolder] = notification.useNotification();
    const navigate = useNavigate();
    const destinations = useDestinations().destinations
    const url = window.location.pathname
    const [destination, setDestination] = useState()
    const destinationId = url.split('/').pop()
    const formRef = useRef(null)

    useEffect(() => {
        if (destinations?.length > 0) {
            const destination = destinations.find(destination => destination.id === destinationId)
            if (destination) {
                setDestination(destination)
            } else {
                handleGetDestination(destinationId)
            }
        } else {
            handleGetDestination(destinationId)
        }
    }, [])

    useEffect(() => {
        if (destination) {
            formRef.current.setFieldsValue({
                destinationName: destination.destinationName,
                tags: destination.tags.split(','),
                destinationDescriptionShort: destination.destinationDescriptionShort,
                destinationDescription: destination.destinationDescription,
                destinationType: destination.destinationType,
                destinationCity: destination.destinationCity,
                destinationCoordinates: destination.destinationCoordinates,
                mapLocation: destination.mapLocation,
                weather: destination.weather,
                recommendedMonths: destination.recommendedMonths.split(','),
                ticketPrice: destination.ticketPrice,
                activeStatus: destination.activeStatus,
            })

            if (destination.destinationImages) {
                const images = destination.destinationImages.split(',')
                const fileList = images.map((url, index) => {
                    return {
                        uid: index,
                        name: `image_${index}.png`,
                        status: 'done',
                        url: url
                    }
                })
                setFileList(fileList)
            }

            if (destination.destinationVideos) {
                const videos = destination.destinationVideos.split(',')
                const fileList = videos.map((url, index) => {
                    return {
                        uid: index,
                        name: `video_${index}.mp4`,
                        status: 'done',
                        url: url
                    }
                })
                setVideoList(fileList)
            }

            if (destination.thumbnailUrl) {
                const fileList = [{
                    uid: 0,
                    name: 'thumbnail.png',
                    status: 'done',
                    url: destination.thumbnailUrl
                }]
                setThumbnailFile(fileList)
            }
        }
    }, [destination])

    const handleGetDestination = async (id) => {
        setLoading(true)
        try {
            const destination = await getDestinationById(id)
            setDestination(destination.data[0])
        } catch (error) {
            console.error('Error fetching destination', error)
        }
        setLoading(false)
    }

    const handlePreview = async (file) => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj);
        }
        setPreviewImage(file.url || file.preview);
        setPreviewOpen(true);
    };
    const handleChange = ({ fileList: newFileList }) => setFileList(newFileList);

    const onVideoChange = ({ fileList: newFileList }) => setVideoList(newFileList);

    const onThumbnailChange = ({ fileList: newFileList }) => setThumbnailFile(newFileList);


    const onFinish = async (values) => {
        topRef.current.scrollTo(0, 0);
        setLoading(true);
        try {
            Object.keys(values).forEach(key => (values[key] === undefined || values[key] === '' || values[key] === null) && delete values[key]);

            let imageUrls = [];
            let videoUrls = [];
            let thumbnailUrl = '';

            const name = values.destinationName.replace(/\s/g, '_').toLowerCase();

            // find new images and list images need to be deleted
            const newImages = fileList.filter(file => !file.url);
            const deletedImages = destination.destinationImages?.split(',').filter(image => !fileList.find(file => file.url === image));

            // find new videos and list videos need to be deleted
            const newVideos = videoList.filter(file => !file.url);
            const deletedVideos = destination.destinationVideos?.split(',').filter(video => !videoList.find(file => file.url === video));

            // find new thumbnail and list thumbnail need to be deleted
            const newThumbnail = thumbnailFile.filter(file => !file.url);
            const deletedThumbnail = destination.thumbnailUrl?.split(',').filter(thumbnail => !thumbnailFile.find(file => file.url === thumbnail));

            if (newImages?.length > 0) {
                setLoadingText('Uploading Destination Images');
                const imageUploadPromises = newImages.map(async (file, index) => {
                    setLoadingText(`Uploading image ${index + 1}`);
                    return handleImageUpload(
                        DESTINATION_CONTAINER,
                        file.originFileObj,
                        file.size,
                        `${name}_${file.name}`
                    ).then((response) => {
                        imageUrls.push(response.blockBlobClient.url);
                    });
                });
                await Promise.all(imageUploadPromises);
            }

            if (newVideos?.length > 0) {
                setLoadingText('Uploading Destination Videos');
                const videoUploadPromises = newVideos.map(async (file, index) => {
                    setLoadingText(`Uploading video ${index + 1}`);
                    return handleImageUpload(
                        DESTINATION_CONTAINER,
                        file.originFileObj,
                        file.size,
                        `${name}_${file.name}`
                    ).then((response) => {
                        videoUrls.push(response.blockBlobClient.url);
                    });
                });
                await Promise.all(videoUploadPromises);
            }

            if (newThumbnail?.length > 0) {
                setLoadingText('Uploading Destination Thumbnail');
                const thumbnailUploadPromises = newThumbnail.map(async (file, index) => {
                    setLoadingText(`Uploading thumbnail ${index + 1}`);
                    return handleImageUpload(
                        DESTINATION_CONTAINER,
                        file.originFileObj,
                        file.size,
                        `${name}_${file.name}`
                    ).then((response) => {
                        thumbnailUrl = response.blockBlobClient.url;
                    });
                });
                await Promise.all(thumbnailUploadPromises);
            }

            // delete blobs
            if (deletedImages?.length > 0) {
                setLoadingText('Deleting Destination Images');
                const imageDeletePromises = deletedImages.map(async (image, index) => {
                    const blobName = getBlobName(image);
                    setLoadingText(`Deleting image ${index + 1}`);
                    return handleDeleteBlob(DESTINATION_CONTAINER, blobName);
                });
                await Promise.all(imageDeletePromises);
            }

            if (deletedVideos?.length > 0) {
                setLoadingText('Deleting Destination Videos');
                const videoDeletePromises = deletedVideos.map(async (video, index) => {
                    const blobName = getBlobName(video);
                    setLoadingText(`Deleting video ${index + 1}`);
                    return handleDeleteBlob(DESTINATION_CONTAINER, blobName);
                });
                await Promise.all(videoDeletePromises);
            }

            if (deletedThumbnail?.length > 0) {
                setLoadingText('Deleting Destination Thumbnail');
                const thumbnailDeletePromises = deletedThumbnail.map(async (thumbnail, index) => {
                    const blobName = getBlobName(thumbnail);
                    setLoadingText(`Deleting thumbnail ${index + 1}`);
                    return handleDeleteBlob(DESTINATION_CONTAINER, blobName);
                });
                await Promise.all(thumbnailDeletePromises);
            }

            let updatedImages = [
                ...fileList.filter((file) => file.url).map((file) => file.url),
                ...imageUrls
            ];

            let updatedVideos = [
                ...videoList.filter((file) => file.url).map((file) => file.url),
                ...videoUrls
            ];

            // Ensure thumbnailUrl is a single string
            let updatedThumbnail = thumbnailUrl || (thumbnailFile[0]?.url || '');

            // remove unwanted parts from urls
            updatedImages.forEach((url, index) => {
                updatedImages[index] = sanitizeBlobUrls(url);
            });

            updatedVideos.forEach((url, index) => {
                updatedVideos[index] = sanitizeBlobUrls(url);
            });

            updatedThumbnail = sanitizeBlobUrls(updatedThumbnail);

            const data = {
                id: destination.id,
                destinationName: values.destinationName,
                tags: values.tags.join(','),
                destinationDescriptionShort: values.destinationDescriptionShort,
                destinationDescription: values.destinationDescription,
                destinationType: values.destinationType.toString(),
                destinationCity: values.destinationCity,
                destinationCoordinates: values.destinationCoordinates,
                mapLocation: values.mapLocation,
                weather: values.weather,
                recommendedMonths: values.recommendedMonths.join(','),
                ticketPrice: values.ticketPrice,
                activeStatus: values.activeStatus,
                destinationImages: updatedImages?.length > 0 ? updatedImages.join(',') : null,
                destinationVideos: updatedVideos?.length > 0 ? updatedVideos.join(',') : null,
                thumbnailUrl: updatedThumbnail === '' ? null : updatedThumbnail
            }

            const response = await editDestination(data);
            if (response.status === 200) {
                api.success({
                    message: 'Destination Updated Successfully',
                    description: 'You have successfully updated the destination'
                });

                setTimeout(() => {
                    navigate(`/dashboard/tours/destinations/${destination.id}`);
                }, 2000);
            } else {
                console.log('Error', response);
                api.error({
                    message: 'Error Updating Destination',
                    description: 'There was an error updating the destination. Please try again'
                });
            }
        } catch (error) {
            console.log('Error', error);
            api.error({
                message: 'Error!',
                description: 'Failed to update the destination. Please try again.',
            });
        }
        setLoading(false);
        setLoadingText('');
    };

    const getBlobName = (blobUrl) => {
        const splitUrl = blobUrl.split('/')
        return splitUrl[splitUrl.length - 1]
    }

    const sanitizeBlobUrls = (blobUrl) => {
        const splitUrl = blobUrl.split('?')
        return splitUrl[0]
    }

    return (
        <div className='add__destination' ref={topRef}>
            {contextHolder}
            <h2>Edit Destination</h2>

            <Spin spinning={loading} tip={loadingText}>
                <Form
                    layout='vertical'
                    onFinish={onFinish}
                    ref={formRef}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label='Destination Name'
                                name='destinationName'
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please enter destination name'
                                    }
                                ]}
                            >
                                <Input placeholder='Destination Name' />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label='Destination Tags'
                                name='tags'
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please add tags'
                                    }
                                ]}
                            >
                                <Select
                                    mode="tags"
                                    style={{
                                        width: '100%',
                                    }}
                                    placeholder="Tags Mode"
                                    options={[]}
                                    maxCount={5}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                label={`Destination Short Description ( ${shortDescriptionCharCount}/1000 characters)`}
                                name='destinationDescriptionShort'
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please enter destination short description'
                                    }
                                ]}
                            >
                                <ReactQuill
                                    theme="snow"
                                    limits={{ maxLength: 500 }}
                                    onChange={(content, delta, source, editor) => {
                                        setShortDescriptionCharCount(editor.getLength())
                                    }}
                                    modules={quillModules}
                                />
                            </Form.Item>
                        </Col>
                    </Row>



                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                label={`Destination Description ( ${longDescriptionCharCount}/10000 characters)`}
                                name='destinationDescription'
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please enter destination short description'
                                    }
                                ]}
                            >
                                <ReactQuill
                                    theme="snow"
                                    limits={{ maxLength: 10000 }}
                                    onChange={(content, delta, source, editor) => {
                                        setLongDescriptionCharCount(editor.getLength())
                                    }}
                                    modules={quillModules}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label='Destination Type'
                                name='destinationType'
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please select destination type'
                                    }
                                ]}
                            >
                                <Select
                                    showSearch
                                    allowClear
                                    style={{
                                        width: '100%',
                                    }}
                                    placeholder="Add Destination Type"
                                    options={destinationOptions}
                                    maxCount={1}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label='Destination City'
                                name='destinationCity'
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please add destination city'
                                    }
                                ]}
                            >
                                <Input placeholder='Destination City' />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label='Destination Coordinates'
                                name='destinationCoordinates'
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please input destination coordinates'
                                    }
                                ]}
                            >
                                <Input placeholder='Destination Coordinates' />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label='Destination Map Location'
                                name='mapLocation'
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please add destination map location'
                                    }
                                ]}
                            >
                                <Input placeholder='Destination Map Location' />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label='Destination Weather'
                                name='weather'
                            >
                                <Select
                                    showSearch
                                    allowClear
                                    style={{
                                        width: '100%',
                                    }}
                                    placeholder="Add weather"
                                    options={weatherOptions}
                                    maxCount={1}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label='Recommended Months'
                                name='recommendedMonths'
                            >
                                <Select
                                    showSearch
                                    mode='multiple'
                                    allowClear
                                    style={{
                                        width: '100%',
                                    }}
                                    placeholder="Add Recommended Months"
                                    options={monthOptions}
                                    maxCount={6}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label='Ticket Price (in USD). Add 0 if free'
                                name='ticketPrice'
                            >
                                <Input type='numbas121er' placeholder='Ticket Price' />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                label='Upload Destination Images (Max 10)'
                                name='destinationImages'
                            >
                                <Upload
                                    listType="picture-card"
                                    fileList={fileList}
                                    onPreview={handlePreview}
                                    onChange={handleChange}
                                    multiple={true}
                                    maxCount={10}
                                    beforeUpload={(file) => {
                                        return false;
                                    }}
                                >
                                    {fileList.length <= 10 && '+ Upload'}
                                </Upload>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                label='Upload Destination Videos (Max 3)'
                                name='destinationVideos'
                            >
                                <Upload
                                    listType="picture"
                                    fileList={videoList}
                                    onChange={onVideoChange}
                                    multiple={true}
                                    maxCount={3}
                                    beforeUpload={(file) => {
                                        return false;
                                    }}
                                >
                                    {videoList?.length < 3 && <Button icon={<PlusOutlined />}>Upload</Button>}
                                </Upload>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                label='Upload Destination Thumbnail Image'
                                name='thumbnailUrl'
                            >
                                <ImgCrop
                                    rotationSlider
                                    modalTitle='Crop Image'
                                    modalOk='Crop'
                                    modalCancel='Cancel'
                                    showReset={true}
                                    aspect={16 / 9}
                                >
                                    <Upload
                                        listType="picture-card"
                                        fileList={thumbnailFile}
                                        onChange={onThumbnailChange}
                                        multiple={false}
                                        beforeUpload={(file) => {
                                            return false;
                                        }}
                                    >
                                        {thumbnailFile.length < 1 && '+ Upload'}
                                    </Upload>
                                </ImgCrop>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Col span={12}>
                        <Form.Item
                            label='Active Status'
                            name='activeStatus'
                            rules={[
                                {
                                    required: true,
                                    message: 'Please select active status'
                                }
                            ]}
                        >
                            <Select
                                showSearch
                                allowClear
                                style={{
                                    width: '100%',
                                }}
                                placeholder="Select Active Status"
                                options={[
                                    {
                                        label: 'Active',
                                        value: ACTIVE
                                    },
                                    {
                                        label: 'Inactive',
                                        value: INACTIVE
                                    },
                                    {
                                        label: 'Pending',
                                        value: PENDING
                                    }
                                ]}
                                maxCount={1}
                            />
                        </Form.Item>
                    </Col>

                    <Form.Item>
                        <Button type='primary' htmlType='submit'>
                            Update Destination
                        </Button>
                    </Form.Item>
                </Form>
            </Spin>

            {previewImage && (
                <Image
                    wrapperStyle={{
                        display: 'none',
                    }}
                    preview={{
                        visible: previewOpen,
                        onVisibleChange: (visible) => setPreviewOpen(visible),
                        afterOpenChange: (visible) => !visible && setPreviewImage(''),
                    }}
                    src={previewImage}
                />
            )}
        </div>
    )
}

export default EditDestination